var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Box, Button, Link, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadUrl } from "@sequentech/ui-core";
import { SettingsContext } from "@/providers/SettingsContextProvider";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { IPermissions } from "@/types/keycloak";
import { FETCH_DOCUMENT } from "@/queries/FetchDocument";
import { hasGraphQLActionErrorCode } from "@/services/graphqlActionError";
/** The file name a downloaded restricted seal record is saved as: its election and area. */
export var sealRecordFileName = function (seal) {
    return "ballot-box-seal-".concat(seal.election_id, "-").concat(seal.area_id, ".json");
};
/**
 * The error code `fetchDocument` answers when the document row or its stored
 * file is gone. Hasura forwards a JSON error's code also with dev mode off.
 */
var DOCUMENT_NOT_FOUND = "DocumentNotFound";
/** Why a restricted record wasn't downloaded. */
var ERecordDownloadFailure;
(function (ERecordDownloadFailure) {
    /** The document is gone: retrying never helps, it is an incident. */
    ERecordDownloadFailure["MISSING"] = "missing";
    /** Anything else: it may work again. */
    ERecordDownloadFailure["ERROR"] = "error";
})(ERecordDownloadFailure || (ERecordDownloadFailure = {}));
/**
 * The seal record of a published ballot box (VOTE-FREEZE). A public record
 * (Seal Record Publication policy Public) is a link to the public bucket. A
 * restricted one is a private event document: an administrator with the
 * document download permission downloads it through a presigned URL; others
 * are told it is restricted.
 */
export var SealRecordLink = function (_a) {
    var electionEventId = _a.electionEventId, seal = _a.seal, areaName = _a.areaName;
    var t = useTranslation().t;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var auth = useContext(AuthContext);
    var tenantId = useTenantStore()[0];
    var _b = useState(null), failed = _b[0], setFailed = _b[1];
    var _c = useLazyQuery(FETCH_DOCUMENT, { fetchPolicy: "no-cache" }), fetchDocument = _c[0], loading = _c[1].loading;
    if (seal.public_path) {
        return (_jsxs(Link, { href: "".concat(globalSettings.PUBLIC_BUCKET_URL).concat(seal.public_path.replace(/^\/+/, "")), target: "_blank", rel: "noopener noreferrer", "aria-label": t("dashboard.ballotBoxes.openRecord", { area: areaName }), sx: { display: "inline-flex", alignItems: "center", gap: 0.5 }, children: [t("dashboard.ballotBoxes.column.record"), _jsx(OpenInNewIcon, { fontSize: "inherit" })] }));
    }
    var documentId = seal.public_document_id ? String(seal.public_document_id) : undefined;
    if (!documentId)
        return _jsx(_Fragment, { children: "\u2014" });
    if (!auth.isAuthorized(true, tenantId, IPermissions.DOCUMENT_DOWNLOAD)) {
        return (_jsx(Typography, { variant: "caption", color: "text.secondary", children: t("dashboard.ballotBoxes.recordRestricted") }));
    }
    var download = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a, data, error, url, error_1, missing;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    setFailed(null);
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, fetchDocument({
                            variables: { electionEventId: electionEventId, documentId: documentId },
                        })];
                case 2:
                    _a = _c.sent(), data = _a.data, error = _a.error;
                    url = (_b = data === null || data === void 0 ? void 0 : data.fetchDocument) === null || _b === void 0 ? void 0 : _b.url;
                    if (error || !url)
                        throw error !== null && error !== void 0 ? error : new Error("The seal record has no download URL");
                    return [4 /*yield*/, downloadUrl(url, sealRecordFileName(seal))];
                case 3:
                    _c.sent();
                    return [3 /*break*/, 5];
                case 4:
                    error_1 = _c.sent();
                    missing = hasGraphQLActionErrorCode(error_1, DOCUMENT_NOT_FOUND);
                    console.error(missing
                        ? "The seal record's document is missing: a published seal's record must stay"
                        : "Downloading the seal record failed", error_1);
                    setFailed(missing ? ERecordDownloadFailure.MISSING : ERecordDownloadFailure.ERROR);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(Box, { component: "span", sx: { display: "inline-flex", flexDirection: "column" }, children: [_jsx(Button, { size: "small", variant: "text", startIcon: _jsx(DownloadIcon, { fontSize: "inherit" }), disabled: loading, onClick: function () { return void download(); }, "aria-label": t("dashboard.ballotBoxes.downloadRecord", { area: areaName }), sx: { justifyContent: "flex-start", px: 0.5, minWidth: 0 }, children: t("dashboard.ballotBoxes.column.record") }), failed ? (_jsx(Typography, { variant: "caption", color: "error", children: failed === ERecordDownloadFailure.MISSING
                    ? t("dashboard.ballotBoxes.recordMissing")
                    : t("dashboard.ballotBoxes.recordError") })) : null] }));
};
export default SealRecordLink;
